// データアクセス層（脱Firebase＝Supabase版）。
// 旧 Firestore 版と同じ関数シグネチャ（listDocs/getOne/upsert/remove/watch）を保ち、
// 既存ビュー（dispatch/dashboard/operations/drivers/vehicles…）は無改修で切替わる。
// テーブルは「id text pk ＋ raw jsonb」。listDocs は {id, ...raw} を返す。
import { supabase, supabaseReady } from '../supabase.js'
import { COLLECTIONS } from './schema.js'

// 論理コレクション名 → 物理テーブル名（snake_case）。mig_import.mjs の MAP と一致させる。
const TABLE = {
  trips: 'trips', tachographs: 'tachographs', customers: 'customers', partners: 'partners',
  drivers: 'drivers', vehicles: 'vehicles', orders: 'orders', tariffs: 'tariffs',
  invoices: 'invoices', imports: 'imports', tripChanges: 'trip_changes',
  chargeSheets: 'charge_sheets', chargeSheetLines: 'charge_sheet_lines',
  settings: 'settings', users: 'users', teikibin: 'teikibin',
  requests: 'requests',
}
const tbl = (name) => TABLE[name] || name

// データ層の準備完了フラグ。かつて Firebase の firebaseReady を参照していた箇所の置き換え先。
// Firebase 自体は 2026-08-24 に撤去済み（Supabase へ一本化）。
export const dataReady = supabaseReady
export const backend = 'supabase'

function ensure() {
  if (!supabaseReady || !supabase) throw new Error('Supabase未接続です（.env の VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY を設定してください）')
}

// {id, raw} 行 → {id, ...raw}（アプリ内は従来どおりフラットなdocで扱う）
const unwrap = (r) => ({ id: r.id, ...(r && r.raw ? r.raw : {}) })

// 簡易キャッシュ（画面遷移ごとの全件再取得を抑制＝軽量化）。書込/リアルタイム変更で無効化＋TTLで自動失効。
// データは常に最新（書込・変更のたびに該当テーブルを破棄→次回読で再取得）。返り値は配列コピー（呼び側のsort等でキャッシュを汚さない）。
const _cache = new Map() // table -> { at, rows }
const CACHE_TTL_MS = 90 * 1000
export function invalidateCache(name) { if (name) _cache.delete(tbl(name)); else _cache.clear() }

// 全件取得（1,000件ごとにページング）。第2引数以降（旧Firestore constraints）は無視＝互換のため受け取るだけ。
export async function listDocs(name, ..._ignored) {
  ensure()
  const table = tbl(name)
  const hit = _cache.get(table)
  if (hit && (Date.now() - hit.at) < CACHE_TTL_MS) return hit.rows.slice()
  const out = []
  let from = 0
  for (;;) {
    const { data, error } = await supabase.from(table).select('id, raw').range(from, from + 999)
    if (error) throw error
    out.push(...(data || []).map(unwrap))
    if (!data || data.length < 1000) break
    from += 1000
  }
  _cache.set(table, { at: Date.now(), rows: out })
  return out.slice()
}

export async function getOne(name, id) {
  ensure()
  const { data, error } = await supabase.from(tbl(name)).select('id, raw').eq('id', id).maybeSingle()
  if (error) throw error
  return data ? unwrap(data) : null
}

// upsert: 旧 setDoc(merge:true) 相当。id指定なら既存rawへマージ、無指定はUUID採番。
// 返り値＝ドキュメントID。
export async function upsert(name, id, data) {
  ensure()
  const table = tbl(name)
  const docId = id || (globalThis.crypto && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}${Math.random().toString(16).slice(2)}`)
  let base = {}
  if (id) {
    const { data: cur, error: e1 } = await supabase.from(table).select('raw').eq('id', docId).maybeSingle()
    if (e1) throw e1
    if (cur && cur.raw) base = cur.raw
  }
  const raw = { ...base, ...data }
  const { error } = await supabase.from(table).upsert({ id: docId, raw }, { onConflict: 'id' })
  if (error) throw error
  _cache.delete(table)
  return docId
}

export async function remove(name, id) {
  ensure()
  const { error } = await supabase.from(tbl(name)).delete().eq('id', id)
  if (error) throw error
  _cache.delete(tbl(name))
}

// 旧 watch 互換：変更のたびに最新の全件を cb へ返す（内部で listDocs 再取得）。
// 大きなテーブルでは重いので、UIのリアルタイムには下の subscribeChanges を推奨。
export function watch(name, cb) {
  ensure()
  let cancelled = false
  const reload = async () => { try { const rows = await listDocs(name); if (!cancelled) cb(rows) } catch (e) { /* noop */ } }
  reload()
  const ch = supabase
    .channel(`rt:${tbl(name)}`)
    .on('postgres_changes', { event: '*', schema: 'public', table: tbl(name) }, () => { _cache.delete(tbl(name)); reload() })
    .subscribe()
  return () => { cancelled = true; try { supabase.removeChannel(ch) } catch (e) { /* noop */ } }
}

// 行レベルのリアルタイム購読（差分反映向け）。
// handler({ type:'INSERT'|'UPDATE'|'DELETE', id, doc })。doc は INSERT/UPDATE 時に {id,...raw}、DELETE時 null。
// 返り値＝購読解除関数。
export function subscribeChanges(name, handler) {
  ensure()
  const table = tbl(name)
  const ch = supabase
    .channel(`rtc:${table}:${Math.random().toString(16).slice(2)}`)
    .on('postgres_changes', { event: '*', schema: 'public', table }, (payload) => {
      _cache.delete(table)
      const rowNew = payload.new && payload.new.id != null ? unwrap(payload.new) : null
      const id = (payload.new && payload.new.id != null) ? payload.new.id : (payload.old ? payload.old.id : null)
      try { handler && handler({ type: payload.eventType, id, doc: rowNew }) } catch (e) { /* noop */ }
    })
    .subscribe()
  return () => { try { supabase.removeChannel(ch) } catch (e) { /* noop */ } }
}

export { COLLECTIONS }
